import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { Form, Formik } from "formik";
import type { DeviceConfigurationValue } from "kubb";
import type React from "react";
import { useMemo, useState } from "react";
import { ErrorBanner } from "../../../components/Error/ErrorBanner";
import AutoSubmit from "../../../components/Forms/AutoSubmit";
import { ButtonGroupField } from "../../../components/Forms/ButtonGroup";
import BlockSpinner from "../../../components/Spinners/BlockSpinner";
import { Button } from "../../../components/Theme/button";
import CSVExportButton from "../../../components/utils/CSVExport/CSVExportButton";
import { formatDate } from "../../../components/utils/HighlightNullAndOldDates";
import { useGetDetailedAssetConfiguration } from "../../../hooks/getEntity";
import { useSelectedCompany } from "../../../reducers/company";

interface AssetConfigurationPageProps {
  asset_id: string;
  serial_number?: string;
}

interface FormFilterValues {
  show: "all" | "set" | "incorrect";
}

const PREFIX_LABELS: Record<string, string> = {
  // Lansen prefixes
  device: "Device Info",
  mbus: "M-Bus Settings",
  mqtt: "MQTT Settings",
  radio: "Radio Settings",
  routing: "Routing",
  modem: "Modem Settings",
  internet: "Internet Settings",
  tls: "TLS Settings",
  security: "Security",
  // Elvaco prefixes
  common: "Common Settings",
  storevalue: "Data Storage",
  reboot: "Reboot Settings",
  report1: "Report 1",
  report2: "Report 2",
  report3: "Report 3",
  report4: "Report 4",
  report5: "Report 5",
  balanceevent: "Balance Event",
  device_list: "Device List",
  fwupdateevent: "Firmware Update Event",
  install: "Installation",
  maintenance: "Maintenance",
  momreport: "MoM Report",
  monitor1: "Monitor 1",
  monitor2: "Monitor 2",
  monitor3: "Monitor 3",
  monitor4: "Monitor 4",
  monitor5: "Monitor 5",
  rebootevent: "Reboot Event",
  swupdateevent: "Software Update Event",
  sysreport: "System Report",
  timesync: "Time Sync",
  // Edge LWM2M objects (Elvaco Edge-External-1.0.3)
  gateway_config: "Gateway Config (33916)",
  mdm_server: "MDM Server (33905)",
  nbiot_status: "NB-IoT Status (33907)",
  nbiot_info: "NB-IoT Info (33909)",
  syslog_config: "Syslog Config (33918)",
  mbus_config: "M-Bus Config (33913)",
  wmbus_config: "wM-Bus Config (33915)",
};

// LWM2M path mapping for Edge devices (single-instance resources)
const LWM2M_PATH_MAP: Record<string, string> = {
  // LwM2M Server (1)
  "lwm2m_server.short_server_id": "/1/0/0",
  "lwm2m_server.lifetime": "/1/0/1",
  "lwm2m_server.default_min_period": "/1/0/2",
  "lwm2m_server.default_max_period": "/1/0/3",
  "lwm2m_server.disable_timeout": "/1/0/5",
  "lwm2m_server.notification_storing": "/1/0/6",
  "lwm2m_server.binding": "/1/0/7",
  // Device (3)
  "device.manufacturer": "/3/0/0",
  "device.model_number": "/3/0/1",
  "device.serial_number": "/3/0/2",
  "device.firmware_version": "/3/0/3",
  "device.available_power_sources": "/3/0/6",
  "device.power_source_voltage": "/3/0/7",
  "device.power_source_current": "/3/0/8",
  "device.battery_level": "/3/0/9",
  "device.memory_free": "/3/0/10",
  "device.error_code": "/3/0/11",
  "device.current_time": "/3/0/13",
  "device.utc_offset": "/3/0/14",
  "device.timezone": "/3/0/15",
  "device.supported_bindings": "/3/0/16",
  "device.device_type": "/3/0/17",
  "device.hardware_version": "/3/0/18",
  "device.software_version": "/3/0/19",
  "device.battery_status": "/3/0/20",
  "device.memory_total": "/3/0/21",
  // Connectivity Monitoring (4)
  "connectivity.network_bearer": "/4/0/0",
  "connectivity.available_network_bearer": "/4/0/1",
  "connectivity.radio_signal_strength": "/4/0/2",
  "connectivity.link_quality": "/4/0/3",
  "connectivity.ip_addresses": "/4/0/4",
  "connectivity.router_ip_addresses": "/4/0/5",
  "connectivity.link_utilization": "/4/0/6",
  "connectivity.apn": "/4/0/7",
  "connectivity.cell_id": "/4/0/8",
  "connectivity.smnc": "/4/0/9",
  "connectivity.smcc": "/4/0/10",
  // Firmware Update (5)
  "firmware_update.package_uri": "/5/0/1",
  "firmware_update.state": "/5/0/3",
  "firmware_update.update_result": "/5/0/5",
  "firmware_update.pkg_name": "/5/0/6",
  "firmware_update.pkg_version": "/5/0/7",
  "firmware_update.protocol_support": "/5/0/8",
  "firmware_update.delivery_method": "/5/0/9",
  // MDM Server (33905)
  "mdm_server.uri": "/33905/0/0",
  "mdm_server.protocol": "/33905/0/1",
  "mdm_server.security_mode": "/33905/0/2",
  "mdm_server.transport_secret_key": "/33905/0/5",
  "mdm_server.connection_config": "/33905/0/10",
  "mdm_server.topic": "/33905/0/11",
  // NB-IoT Status (33907)
  "nbiot_status.uptime": "/33907/0/0",
  "nbiot_status.avg_current": "/33907/0/1",
  "nbiot_status.network_class": "/33907/0/2",
  "nbiot_status.ecl": "/33907/0/3",
  "nbiot_status.rssi": "/33907/0/4",
  "nbiot_status.snr": "/33907/0/5",
  "nbiot_status.mdm_conn_status": "/33907/0/10",
  "nbiot_status.radio_band": "/33907/0/11",
  "nbiot_status.access_technology": "/33907/0/12",
  // NB-IoT Info (33909)
  "nbiot_info.imsi": "/33909/0/0",
  "nbiot_info.iccid": "/33909/0/1",
  "nbiot_info.registrations": "/33909/0/2",
  "nbiot_info.last_reg_duration": "/33909/0/3",
  "nbiot_info.modem_model": "/33909/0/4",
  "nbiot_info.modem_firmware": "/33909/0/5",
  "nbiot_info.reg_uptime": "/33909/0/6",
  "nbiot_info.modem_fota_result": "/33909/0/8",
  "nbiot_info.imei": "/33909/0/9",
  // Gateway Config (33916)
  "gateway_config.nfc_enabled": "/33916/0/4",
  "gateway_config.nfc_config_locked": "/33916/0/5",
  "gateway_config.historic_resend_status": "/33916/0/14",
  "gateway_config.config_write_status": "/33916/0/16",
  "gateway_config.meter_uploads_per_tx": "/33916/0/21",
  "gateway_config.dtls_min_timeout": "/33916/0/22",
  "gateway_config.dtls_max_timeout": "/33916/0/23",
  "gateway_config.mqttsn_comm_timeout": "/33916/0/24",
  "gateway_config.mqttsn_comm_attempts": "/33916/0/25",
  "gateway_config.coap_ack_timeout": "/33916/0/30",
  "gateway_config.coap_max_retransmit": "/33916/0/31",
  "gateway_config.iowa_dtls_min_timeout": "/33916/0/32",
  "gateway_config.iowa_dtls_max_timeout": "/33916/0/33",
  "gateway_config.iowa_comm_retry_count": "/33916/0/34",
  "gateway_config.iowa_comm_retry_delay": "/33916/0/35",
  "gateway_config.iowa_comm_seq_retry_count": "/33916/0/36",
  "gateway_config.iowa_comm_seq_retry_delay": "/33916/0/37",
  "gateway_config.network_conn_max_holdoff": "/33916/0/38",
  "gateway_config.network_search_period": "/33916/0/39",
  "gateway_config.modem_restart_backoff": "/33916/0/40",
  "gateway_config.mdm_reconnect_backoff": "/33916/0/41",
  "gateway_config.lwm2m_resume_backoff": "/33916/0/42",
  "gateway_config.auto_upload_age_limit": "/33916/0/44",
  "gateway_config.auto_upload_order": "/33916/0/45",
  "gateway_config.time_sync_source": "/33916/0/46",
  "gateway_config.mdm_comm_failures": "/33916/0/47",
  "gateway_config.upload_protocol": "/33916/0/48",
  "gateway_config.use_psm": "/33916/0/49",
  "gateway_config.edrx_mode": "/33916/0/50",
  "gateway_config.enable_rai": "/33916/0/51",
  "gateway_config.power_source": "/33916/0/52",
  "gateway_config.nbiot_radio_bands": "/33916/0/53",
  "gateway_config.mqtt_keepalive_timeout": "/33916/0/55",
  "gateway_config.reboot_required": "/33916/0/56",
  "gateway_config.lwm2m_forced_queue_mode": "/33916/0/57",
  "gateway_config.modem_antenna_config": "/33916/0/58",
  "gateway_config.expansion_top_config": "/33916/0/59",
  "gateway_config.expansion_bottom_config": "/33916/0/60",
  "gateway_config.dtls_handshake_max_rewinds": "/33916/0/61",
  "gateway_config.modem_access_technology": "/33916/0/63",
  "gateway_config.fast_response_mode_timeout": "/33916/0/64",
  "gateway_config.fast_response_mode_active": "/33916/0/65",
  "gateway_config.health_monitor_timeout": "/33916/0/66",
  "gateway_config.ntp_server": "/33916/0/67",
  "gateway_config.ntp_server_port": "/33916/0/68",
  "gateway_config.modem_stk_mode": "/33916/0/69",
  "gateway_config.readout_schedules": "/33916/0/70",
  "gateway_config.report_schedules": "/33916/0/71",
  "gateway_config.tx_schedule": "/33916/0/72",
  "gateway_config.nfc_auto_lock_enable": "/33916/0/75",
  "gateway_config.nfc_auto_lock_timeout": "/33916/0/76",
  "gateway_config.bootstrap_uri": "/33916/0/77",
  // Syslog Config (33918)
  "syslog_config.mqttsn_topic": "/33918/0/0",
  "syslog_config.storage_level": "/33918/0/1",
  "syslog_config.auto_upload_level": "/33918/0/2",
  "syslog_config.auto_upload_age_limit": "/33918/0/3",
};

// Multi-instance resource ID mappings (field_name → resource_id)
const MULTI_INSTANCE_RESOURCES: Record<
  string,
  Record<string, [number, number]>
> = {
  mbus_config: {
    device_list: [33913, 5],
    poweron_delay: [33913, 6],
    init_delay: [33913, 7],
    request_delay: [33913, 8],
    collision_delay: [33913, 9],
    response_timeout: [33913, 10],
    select_retries: [33913, 11],
    ud2_retries: [33913, 12],
    always_on: [33913, 13],
    runbefore_command: [33913, 14],
    multi_telegram_enable: [33913, 15],
    multi_telegram_read_limit: [33913, 16],
    statistics: [33913, 17],
    bus_status: [33913, 18],
    bus_load: [33913, 19],
    estimated_t: [33913, 20],
    maximum_t: [33913, 21],
    response_count: [33913, 22],
    device_count: [33913, 23],
  },
  wmbus_config: {
    device_list: [33915, 6],
    mode: [33915, 7],
    antenna: [33915, 8],
    filter_list: [33915, 11],
    statistics: [33915, 12],
    max_age: [33915, 13],
    receive_window_schedules: [33915, 14],
    discovery_rssi_limit: [33915, 15],
    discovery_schedule: [33915, 16],
    assign_default_schedule: [33915, 17],
  },
  transaction_stats: {
    transactions: [33910, 1],
    retransmissions: [33910, 2],
    lost_transactions: [33910, 3],
    avg_response_time: [33910, 4],
    min_response_time: [33910, 5],
    max_response_time: [33910, 6],
  },
  meter_data: {
    message_type: [33911, 0],
    message_encoding: [33911, 1],
    message_data: [33911, 2],
  },
  expansion_port: {
    manufacturer: [33917, 0],
    model: [33917, 1],
    serial_number: [33917, 2],
    hardware_version: [33917, 3],
    firmware_version: [33917, 4],
    bootloader_version: [33917, 5],
    error_code: [33917, 6],
  },
};

function getLwm2mPath(configKey: string): string | null {
  // Check single-instance map first
  if (configKey in LWM2M_PATH_MAP) {
    return LWM2M_PATH_MAP[configKey];
  }
  // Check multi-instance keys (e.g., "mbus_config.0.device_list")
  const parts = configKey.split(".");
  if (parts.length === 3) {
    const [prefix, instanceStr, field] = parts;
    const multiMap = MULTI_INSTANCE_RESOURCES[prefix];
    if (multiMap?.[field]) {
      const [objectId, resourceId] = multiMap[field];
      return `/${objectId}/${instanceStr}/${resourceId}`;
    }
  }
  return null;
}

function groupConfigByPrefix(
  values: DeviceConfigurationValue[],
): Map<string, DeviceConfigurationValue[]> {
  const groups = new Map<string, DeviceConfigurationValue[]>();
  for (const value of values) {
    const prefix = value.key.split(".")[0];
    if (!groups.has(prefix)) {
      groups.set(prefix, []);
    }
    groups.get(prefix)?.push(value);
  }
  return groups;
}

export function getFilterFn(
  filterShow: "all" | "set" | "incorrect",
): (v: DeviceConfigurationValue) => boolean {
  if (filterShow === "all") {
    return () => true;
  }
  if (filterShow === "incorrect") {
    return (v) => v.target !== null && v.in_sync === false;
  }
  return (v) => v.target !== null;
}

export function buildCsvRows(
  values: DeviceConfigurationValue[],
  filterFn: (v: DeviceConfigurationValue) => boolean,
) {
  return values.filter(filterFn).map((v) => {
    const prefix = v.key.split(".")[0];
    return {
      Group: PREFIX_LABELS[prefix] ?? prefix,
      Setting: v.key,
      "LWM2M Path": getLwm2mPath(v.key) ?? "",
      Target: v.target ?? "",
      Current: v.current ?? "",
      "In Sync": v.target == null ? "N/A" : v.in_sync ? "true" : "false",
      "Last Changed": v.last_changed ?? "",
      "Last Checked": (v as any).last_checked ?? "",
    };
  });
}

function ConfigGroup({
  prefix,
  values,
  filterFn,
  forceOpen,
}: {
  prefix: string;
  values: DeviceConfigurationValue[];
  filterFn: (v: DeviceConfigurationValue) => boolean;
  forceOpen: boolean | null;
}) {
  const filteredValues = values.filter(filterFn);
  if (filteredValues.length === 0) return null;

  const hasIncorrect = filteredValues.some(
    (v) => v.target != null && v.in_sync === false,
  );
  const label = PREFIX_LABELS[prefix] || prefix;

  const isOpen = forceOpen !== null ? forceOpen : hasIncorrect;

  return (
    <Disclosure as="div" defaultOpen={isOpen} className="mb-2">
      <DisclosureButton className="group flex w-full items-center gap-x-2 rounded-md bg-gray-100 px-3 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-200">
        <ChevronRightIcon
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-gray-500 transition-transform group-data-[open]:rotate-90"
        />
        <span className="flex-1">
          {label}{" "}
          <span className="text-gray-500">({filteredValues.length})</span>
        </span>
        {hasIncorrect && (
          <FontAwesomeIcon icon={faXmark} className="text-danger" />
        )}
      </DisclosureButton>
      <DisclosurePanel className="mt-1 ml-6">
        <table className="w-full text-left text-sm table-fixed">
          <colgroup>
            <col style={{ width: "260px" }} />
            <col style={{ width: "150px" }} />
            <col style={{ width: "170px" }} />
            <col style={{ width: "150px" }} />
            <col style={{ width: "150px" }} />
          </colgroup>
          <thead>
            <tr className="border-b border-zinc-950/10">
              <th className="py-2 px-3 font-medium text-zinc-500">Setting</th>
              <th className="py-2 px-3 font-medium text-zinc-500">Target</th>
              <th className="py-2 px-3 font-medium text-zinc-500">Current</th>
              <th className="py-2 px-3 font-medium text-zinc-500">
                Last checked
              </th>
              <th className="py-2 px-3 font-medium text-zinc-500">
                Last changed
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredValues.map((value, idx) => {
              return (
                <tr
                  key={value.key}
                  className={idx % 2 === 1 ? "bg-zinc-950/[2.5%]" : ""}
                >
                  <td className="py-2 px-3 align-top overflow-hidden break-all">
                    {value.key}
                    {(() => {
                      const lwm2mPath = getLwm2mPath(value.key);
                      return lwm2mPath ? (
                        <span className="block text-xs text-zinc-400 font-mono">
                          {lwm2mPath}
                        </span>
                      ) : null;
                    })()}
                  </td>
                  <td
                    className="py-2 px-3 align-top overflow-hidden"
                    style={{ overflowWrap: "anywhere", whiteSpace: "pre-line" }}
                  >
                    {value.target || "-"}
                  </td>
                  <td
                    className="py-2 px-3 align-top overflow-hidden"
                    style={{ overflowWrap: "anywhere", whiteSpace: "pre-line" }}
                  >
                    <span className="inline-flex items-start gap-1">
                      <span style={{ overflowWrap: "anywhere" }}>
                        {value.current || "-"}
                      </span>
                      {value.target &&
                        value.current &&
                        (value.in_sync ? (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className={"text-success shrink-0 mt-1"}
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faXmark}
                            className={"text-danger shrink-0 mt-1"}
                          />
                        ))}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-nowrap align-top">
                    {formatDate(value.last_checked)}
                  </td>
                  <td className="py-2 px-3 text-nowrap align-top">
                    {formatDate(value.last_changed)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </DisclosurePanel>
    </Disclosure>
  );
}

export default function AssetConfigurationPage(
  props: AssetConfigurationPageProps,
): React.ReactElement {
  const company = useSelectedCompany();

  const [filterValues, setFilterValues] = useState<FormFilterValues>({
    show: "all",
  });

  // null = default (auto-expand incorrect), true = all expanded, false = all collapsed
  const [expandAll, setExpandAll] = useState<boolean | null>(null);
  // Key to force re-render of Disclosure components when expandAll changes
  const [expandKey, setExpandKey] = useState(0);

  const { data, loading, error } = useGetDetailedAssetConfiguration(
    props.asset_id,
    company?.company_id,
  );

  const groupedData = useMemo(() => {
    if (!data?.values) return new Map<string, DeviceConfigurationValue[]>();
    return groupConfigByPrefix(data.values);
  }, [data?.values]);

  const filterFn = getFilterFn(filterValues.show);

  const csvRows = useMemo(() => {
    if (!data?.values) return [];
    return buildCsvRows(data.values, getFilterFn(filterValues.show));
  }, [data?.values, filterValues.show]);

  const csvFilename = props.serial_number
    ? `config-detail-${props.serial_number}-${new Date().toISOString().slice(0, 10)}`
    : `config-detail-${new Date().toISOString().slice(0, 10)}`;

  const total = data?.values.length;
  const set = data?.values.filter((v) => v.target !== null).length;
  const incorrect = data?.values.filter(
    (v) => v.target !== null && v.in_sync === false,
  ).length;

  return (
    <BlockSpinner loading={loading}>
      <div>
        <div className="flex flex-wrap items-end gap-4 w-full">
          <Formik
            initialValues={filterValues}
            onSubmit={(v) => {
              setFilterValues(v);
            }}
            enableReinitialize={true}
          >
            {() => {
              return (
                <>
                  <AutoSubmit />
                  <Form>
                    <ButtonGroupField
                      fieldName={"show"}
                      label={{}}
                      options={[
                        { label: `All (${total || "-"})`, value: "all" },
                        { label: `Set (${set || "-"})`, value: "set" },
                        {
                          label: `Incorrect (${incorrect || "-"})`,
                          value: "incorrect",
                        },
                      ]}
                    />
                  </Form>
                </>
              );
            }}
          </Formik>
          <div className="py-2 flex-1">
            <div className="mt-2 flex gap-2">
              <Button
                color="brandLight"
                className="h-[38px]"
                onClick={() => {
                  setExpandAll(true);
                  setExpandKey((k) => k + 1);
                }}
              >
                Expand All
              </Button>
              <Button
                color="brandLight"
                className="h-[38px]"
                onClick={() => {
                  setExpandAll(false);
                  setExpandKey((k) => k + 1);
                }}
              >
                Collapse All
              </Button>
              <CSVExportButton
                className="h-[38px] ml-auto"
                data={csvRows}
                filename={csvFilename}
                columnOrder={[
                  "Group",
                  "Setting",
                  "LWM2M Path",
                  "Target",
                  "Current",
                  "In Sync",
                  "Last Changed",
                  "Last Checked",
                ]}
                sortColumns={false}
                disabled={csvRows.length === 0}
              />
            </div>
          </div>
        </div>
        <>
          {error && <ErrorBanner error={error} />}
          {data && (
            <div className="mt-4 space-y-2">
              {Array.from(groupedData.entries()).map(([prefix, values]) => (
                <ConfigGroup
                  key={`${prefix}-${expandKey}`}
                  prefix={prefix}
                  values={values}
                  filterFn={filterFn}
                  forceOpen={expandAll}
                />
              ))}
            </div>
          )}
        </>
      </div>
    </BlockSpinner>
  );
}
